'use client'

/**
 * Switch the viewer's active org, then refresh (#15618).
 *
 * The one client path into the EXISTING `switchOrg` server action, shared by
 * the header's `OrgSwitcherDropdown` and the side navigation's account menu.
 * It was the dropdown's own `handleSwitchOrg` until #15618 and moved here
 * verbatim, so the two switchers toast, refresh and fail identically — a
 * second hand-written copy is how one of them would stop calling
 * `router.refresh()`, and that failure is silent (see below).
 *
 * `switchOrg` keeps every guarantee it had: an ACTIVE membership check through
 * `withAuthCrossOrg`, and the `org.switched` event.
 */

import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'

import { switchOrg } from '@/app/actions/switch-org'
import type { UserOrg } from '@/lib/get-user-orgs'
import { toOrgId } from '@/lib/org-ids'
import { toast, toastError } from '@/lib/toast'

export function useSwitchOrg(
  orgs: readonly Pick<UserOrg, 'orgId' | 'orgName'>[],
  currentOrgId: string,
) {
  const router = useRouter()
  const [isSwitching, setIsSwitching] = useState(false)

  /**
   * Resolves once the switch settled — `true` when the viewer is now in
   * `orgId` (a no-op for the current org), so a caller that navigates into
   * the org afterwards (#16445) can stay put when it failed.
   */
  const switchTo = useCallback(
    async (orgId: string): Promise<boolean> => {
      if (orgId === currentOrgId) return true

      setIsSwitching(true)
      const result = await switchOrg(toOrgId(orgId))

      if (result.success) {
        const targetOrg = orgs.find(o => o.orgId === orgId)
        toast.success(`Switched to ${targetOrg?.orgName ?? 'organization'}`)
        // #11947 review issue 6. `switchOrg` calls `revalidatePath('/',
        // 'layout')`, which Next serves by re-rendering in the SAME request as
        // the action — and `auth()` is memoized per request, so that re-render
        // can reuse the pre-switch session and derive the old org. An explicit
        // refresh puts the re-render in a fresh request, where the memo is a
        // fresh slot. It is also what re-renders the side navigation for the
        // new org (#15618): the root layout and its sidebar re-resolve the
        // new org's chrome.
        //
        // Kept even though the reviewer could not reproduce it: refreshing
        // after an org switch is correct whether or not the memo interacts
        // this way, and the failure it guards against is silent — the user
        // stays in the old org with no error to explain why.
        router.refresh()
        return true
      }
      toastError({
        message: result.error ?? 'Failed to switch organization',
        context: { action: 'switchOrg', orgId },
      })
      setIsSwitching(false)
      return false
    },
    [currentOrgId, orgs, router],
  )

  return { switchTo, isSwitching }
}
